/**
 * format.js — Currency and date formatting utilities
 *
 * Pure formatting helpers used across all pages and components.
 * No side effects, no dependencies — just string transformations.
 */

/**
 * Format a number as US currency string.
 * @param {number} amount - Dollar amount (can be negative)
 * @param {boolean} [showCents=true] - Whether to show cents
 * @returns {string} Formatted string like "$1,234.56" or "-$500"
 */
export function formatCurrency(amount, showCents = true) {
  if (amount == null || isNaN(amount)) return '$0.00';
  const abs = Math.abs(amount);
  const formatted = abs.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  });
  return amount < 0 ? `-${formatted}` : formatted;
}

/**
 * Format a number as compact currency (no cents, K suffix for thousands).
 * Used in gauges and summary cards where space is tight.
 * @param {number} amount - Dollar amount
 * @returns {string} e.g., "$1.2K", "$800", "$12.5K"
 */
export function formatCompact(amount) {
  if (amount == null || isNaN(amount)) return '$0';
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';
  if (abs >= 1000) {
    const k = abs / 1000;
    return `${sign}$${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}K`;
  }
  return `${sign}$${Math.round(abs)}`;
}

/**
 * Format a Date or ISO string as a short display date.
 * @param {Date|string} date - Date object or ISO string
 * @param {string} [style='short'] - 'short' = "Mar 3", 'full' = "March 3, 2026", 'relative' = "2 days ago"
 * @returns {string} Formatted date string
 */
export function formatDate(date, style = 'short') {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  if (style === 'relative') {
    return formatRelativeDate(d);
  }

  if (style === 'full') {
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }

  // 'short' — "Mar 3"
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Format a date as a relative string ("today", "yesterday", "3 days ago", etc.)
 * @param {Date} date - Date to format
 * @returns {string} Relative date string
 */
function formatRelativeDate(date) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((today - target) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays === -1) return 'Tomorrow';
  if (diffDays < 0) return `In ${Math.abs(diffDays)} days`;
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return formatDate(date, 'short');
}

/**
 * Get month key string from a Date (used as Firestore document IDs).
 * @param {Date} [date] - Date to extract month from (defaults to now)
 * @returns {string} "YYYY-MM" format, e.g., "2026-03"
 */
export function getMonthKey(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/**
 * Shift a month key (YYYY-MM) by N months.
 * @param {string} monthKey - e.g., "2026-03"
 * @param {number} delta - Months to shift (positive = forward, negative = back)
 * @returns {string} Shifted month key
 */
export function shiftMonthKey(monthKey, delta) {
  const [y, m] = monthKey.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Format a month key as a readable label.
 * @param {string} monthKey - e.g., "2026-03"
 * @returns {string} e.g., "March 2026"
 */
export function formatMonthLabel(monthKey) {
  const [y, m] = monthKey.split('-').map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/**
 * Get ordinal suffix for a day number.
 * @param {number} day - Day of month (1-31)
 * @returns {string} Day with suffix, e.g., "1st", "2nd", "13th"
 */
export function ordinalDay(day) {
  if (day >= 11 && day <= 13) return `${day}th`;
  switch (day % 10) {
    case 1: return `${day}st`;
    case 2: return `${day}nd`;
    case 3: return `${day}rd`;
    default: return `${day}th`;
  }
}
